import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PinKeypad from "./PinKeypad";
import { Mail, KeyRound, ChevronLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * PIN recovery — request a 6-digit code by email, then set a new PIN.
 * If the backend is running in simulated mode, the code is written to
 * server logs (the user is told to check with the admin).
 */
export default function PinRecoveryDialog({ open, onClose, onDone }) {
  const [phase, setPhase] = useState("intro"); // intro | code | newpin
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [code, setCode] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [step2Sub, setStep2Sub] = useState("new"); // new | confirm

  const reset = () => {
    setPhase("intro"); setError(""); setInfo("");
    setCode(""); setNewPin(""); setConfirmPin(""); setStep2Sub("new");
  };
  const close = () => { reset(); onClose?.(); };

  const requestCode = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/security/pin/recover/request");
      setInfo(`Hemos enviado un código de 6 dígitos a ${data.sent_to}. Caduca en ${data.ttl_minutes} minutos. Si el envío por email aún no está configurado, el código estará disponible en el registro del servidor.`);
      setPhase("code");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "No se pudo iniciar la recuperación");
    } finally {
      setBusy(false);
    }
  };

  const submitCode = (val) => {
    if (val.length !== 6) return;
    setCode(val);
    setError("");
    setPhase("newpin");
    setStep2Sub("new");
  };

  const submitNewPin = (val) => {
    if (val.length !== 4) return;
    setNewPin(val);
    setError("");
    setStep2Sub("confirm");
    setConfirmPin("");
  };

  const submitConfirm = async (val) => {
    if (val.length !== 4) return;
    if (val !== newPin) {
      setError("Los PIN no coinciden.");
      setConfirmPin("");
      return;
    }
    setBusy(true); setError("");
    try {
      await api.post("/security/pin/recover/confirm", { code, new_pin: newPin });
      toast.success("PIN restablecido");
      onDone?.();
      close();
    } catch (err) {
      const status = err?.response?.status;
      const detail = formatApiError(err?.response?.data?.detail) || "Error";
      setError(detail);
      if (status === 401) {
        // bad code — go back to code entry
        setTimeout(() => { setCode(""); setNewPin(""); setConfirmPin(""); setStep2Sub("new"); setPhase("code"); }, 900);
      } else {
        setConfirmPin("");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="pin-recovery-dialog">
        <div className="bg-gradient-to-br from-orange-50 to-white px-6 pt-6 pb-4 border-b border-orange-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-white shadow-sm">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <DialogHeader className="p-0 space-y-0">
                <DialogTitle className="text-base font-bold text-neutral-900">Recuperar PIN</DialogTitle>
                <DialogDescription className="text-xs text-neutral-600">
                  Envío de código al correo del administrador
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="mb-3 flex items-center justify-between">
            {phase !== "intro" ? (
              <button
                onClick={() => { setError(""); if (phase === "newpin") setPhase("code"); else if (phase === "code") setPhase("intro"); }}
                className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
                data-testid="pin-recovery-back"
              >
                <ChevronLeft className="h-3 w-3" /> Volver
              </button>
            ) : <span />}
            <button
              onClick={close}
              className="text-xs text-neutral-500 hover:text-neutral-900"
              data-testid="pin-recovery-close"
            >
              Cerrar
            </button>
          </div>

          {phase === "intro" && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-700">
                Enviaremos un código de 6 dígitos al correo del administrador. Con ese código podrás restablecer tu PIN.
              </p>
              <button
                onClick={requestCode}
                disabled={busy}
                className="btn-primary w-full"
                data-testid="pin-recovery-request"
              >
                {busy ? "Enviando…" : "Enviarme un código"}
              </button>
            </div>
          )}

          {phase === "code" && (
            <div className="space-y-3">
              {info && (
                <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800" data-testid="pin-recovery-info">
                  {info}
                </div>
              )}
              <div className="text-sm text-neutral-700">Introduce el código de 6 dígitos recibido.</div>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => { if (e.key === "Enter" && code.length === 6) submitCode(code); }}
                className="w-full rounded-lg border border-neutral-300 bg-white px-4 h-12 text-center text-lg tracking-[0.4em] font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="••••••"
                data-testid="pin-recovery-code-input"
              />
              <button
                onClick={() => submitCode(code)}
                disabled={code.length !== 6 || busy}
                className="btn-primary w-full"
                data-testid="pin-recovery-code-submit"
              >
                Continuar
              </button>
            </div>
          )}

          {phase === "newpin" && (
            <div className="space-y-3">
              <div className="text-sm text-neutral-700">
                {step2Sub === "new" ? "Elige tu nuevo PIN (4 dígitos)." : "Confirma tu nuevo PIN."}
              </div>
              {step2Sub === "new" ? (
                <PinKeypad
                  value={newPin}
                  onChange={(v) => { setNewPin(v); setError(""); }}
                  onSubmit={submitNewPin}
                  disabled={busy}
                  testIdPrefix="pin-recovery-new"
                />
              ) : (
                <PinKeypad
                  value={confirmPin}
                  onChange={(v) => { setConfirmPin(v); setError(""); }}
                  onSubmit={submitConfirm}
                  disabled={busy}
                  testIdPrefix="pin-recovery-confirm"
                />
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-start gap-2" data-testid="pin-recovery-error">
              <KeyRound className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
